import { useCallback, useEffect, useMemo, useState, type ComponentProps } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { completeEntraCallback, createBrowserAuth, signInWithEntra } from './auth';
import type { CrmApiClient } from './crm-api';
import { loadCrmApiFactory, type CrmApiFactory } from './api-client-loader';
import { AppShell } from './components/AppShell';
import type { CreationLauncherProps } from './components/CreationLauncher';
import type { ContextCreateKind } from './contextual-create';
import type { GlobalSearch as GlobalSearchImplementation } from './components/GlobalSearch';
import { hasCapability } from '@resilience/domain';
import { readableCode } from './presentation';
import { AsyncState } from './components/AsyncState';
import { WorkspaceErrorBoundary } from './components/WorkspaceErrorBoundary';
import type { PublicIntakeEntryProps } from './components/PublicIntakeEntry';
import { publicOnboardingTokenFromHash, routeFromHash, type AppRoute } from './routes';
import { canNavigate, rejectedNavigationHash } from './navigation-guard';

import { deferWorkspace } from './defer-workspace';
const GlobalSearch = deferWorkspace<ComponentProps<typeof GlobalSearchImplementation>>(
  () => import('./components/GlobalSearch').then((module) => ({ default: module.GlobalSearch })),
  {
    loadingContent: (
      <div className="global-search">
        <input disabled aria-label="Search CRM — loading search" placeholder="Search CRM…" />
      </div>
    ),
    errorContent: (retry) => (
      <div className="global-search" role="status">
        <button type="button" className="button button--quiet" onClick={retry}>
          Retry search download
        </button>
      </div>
    ),
  },
);
const CreationLauncher = deferWorkspace<CreationLauncherProps>(
  () =>
    import('./components/CreationLauncher').then((module) => ({
      default: module.CreationLauncher,
    })),
  { loadingMessage: 'Loading creation controls...', offerReload: false },
);
const PublicIntakeEntry = deferWorkspace<PublicIntakeEntryProps>(
  () => import('./components/PublicIntakeEntry'),
);
import type { DirectoryWorkspace as DirectoryWorkspaceImplementation } from './components/DirectoryWorkspace';
import type { MigrationWorkspace as MigrationWorkspaceImplementation } from './components/MigrationWorkspace';
import type { OnboardingWorkspace as OnboardingWorkspaceImplementation } from './components/OnboardingWorkspace';
import type { OverviewDashboard as OverviewDashboardImplementation } from './components/OverviewDashboard';
import type { OpportunityWorkspace as OpportunityWorkspaceImplementation } from './components/OpportunityWorkspace';
import type { ReportsWorkspace as ReportsWorkspaceImplementation } from './components/ReportsWorkspace';
import type { PipelineReviewWorkspace as PipelineReviewWorkspaceImplementation } from './components/PipelineReviewWorkspace';
import type { PublicOnboardingForm as PublicOnboardingFormImplementation } from './components/PublicOnboardingForm';
import type { PublicCreditForm as PublicCreditFormImplementation } from './components/PublicCreditForm';
import type { XeroReconciliationWorkspace as XeroReconciliationWorkspaceImplementation } from './components/XeroReconciliationWorkspace';
import type { TaskWorkspace as TaskWorkspaceImplementation } from './components/TaskWorkspace';
import type { CatalogWorkspace as CatalogWorkspaceImplementation } from './components/CatalogWorkspace';
import type { CapabilityWorkspace as CapabilityWorkspaceImplementation } from './components/CapabilityWorkspace';
import type { AdministrationWorkspace as AdministrationWorkspaceImplementation } from './components/AdministrationWorkspace';
import type { AuditTrailWorkspace as AuditTrailWorkspaceImplementation } from './components/AuditTrailWorkspace';

const DirectoryWorkspace = deferWorkspace<ComponentProps<typeof DirectoryWorkspaceImplementation>>(
  () =>
    import('./components/DirectoryWorkspace').then((module) => ({
      default: module.DirectoryWorkspace,
    })),
);
const MigrationWorkspace = deferWorkspace<ComponentProps<typeof MigrationWorkspaceImplementation>>(
  () =>
    import('./components/MigrationWorkspace').then((module) => ({
      default: module.MigrationWorkspace,
    })),
);
const OnboardingWorkspace = deferWorkspace<
  ComponentProps<typeof OnboardingWorkspaceImplementation>
>(() =>
  import('./components/OnboardingWorkspace').then((module) => ({
    default: module.OnboardingWorkspace,
  })),
);
const OverviewDashboard = deferWorkspace<ComponentProps<typeof OverviewDashboardImplementation>>(
  () =>
    import('./components/OverviewDashboard').then((module) => ({
      default: module.OverviewDashboard,
    })),
);
const OpportunityWorkspace = deferWorkspace<
  ComponentProps<typeof OpportunityWorkspaceImplementation>
>(() =>
  import('./components/OpportunityWorkspace').then((module) => ({
    default: module.OpportunityWorkspace,
  })),
);
const ReportsWorkspace = deferWorkspace<ComponentProps<typeof ReportsWorkspaceImplementation>>(() =>
  import('./components/ReportsWorkspace').then((module) => ({ default: module.ReportsWorkspace })),
);
const PipelineReviewWorkspace = deferWorkspace<
  ComponentProps<typeof PipelineReviewWorkspaceImplementation>
>(() =>
  import('./components/PipelineReviewWorkspace').then((module) => ({
    default: module.PipelineReviewWorkspace,
  })),
);
const PublicOnboardingForm = deferWorkspace<
  ComponentProps<typeof PublicOnboardingFormImplementation>
>(() =>
  import('./components/PublicOnboardingForm').then((module) => ({
    default: module.PublicOnboardingForm,
  })),
);
const PublicCreditForm = deferWorkspace<ComponentProps<typeof PublicCreditFormImplementation>>(() =>
  import('./components/PublicCreditForm').then((module) => ({ default: module.PublicCreditForm })),
);
const XeroReconciliationWorkspace = deferWorkspace<
  ComponentProps<typeof XeroReconciliationWorkspaceImplementation>
>(() =>
  import('./components/XeroReconciliationWorkspace').then((module) => ({
    default: module.XeroReconciliationWorkspace,
  })),
);
const TaskWorkspace = deferWorkspace<ComponentProps<typeof TaskWorkspaceImplementation>>(() =>
  import('./components/TaskWorkspace').then((module) => ({ default: module.TaskWorkspace })),
);
const CatalogWorkspace = deferWorkspace<ComponentProps<typeof CatalogWorkspaceImplementation>>(() =>
  import('./components/CatalogWorkspace').then((module) => ({ default: module.CatalogWorkspace })),
);
const CapabilityWorkspace = deferWorkspace<
  ComponentProps<typeof CapabilityWorkspaceImplementation>
>(() =>
  import('./components/CapabilityWorkspace').then((module) => ({
    default: module.CapabilityWorkspace,
  })),
);
const AdministrationWorkspace = deferWorkspace<
  ComponentProps<typeof AdministrationWorkspaceImplementation>
>(() =>
  import('./components/AdministrationWorkspace').then((module) => ({
    default: module.AdministrationWorkspace,
  })),
);
const AuditTrailWorkspace = deferWorkspace<
  ComponentProps<typeof AuditTrailWorkspaceImplementation>
>(() =>
  import('./components/AuditTrailWorkspace').then((module) => ({
    default: module.AuditTrailWorkspace,
  })),
);

let configuredBrowserAuth: SupabaseClient | undefined;
const routeWorkspaces: Partial<Record<AppRoute, { preload: () => Promise<void> }>> = {
  overview: OverviewDashboard,
  organisations: DirectoryWorkspace,
  pipeline: PipelineReviewWorkspace,
  opportunities: OpportunityWorkspace,
  tasks: TaskWorkspace,
  reports: ReportsWorkspace,
  catalogue: CatalogWorkspace,
  team: CapabilityWorkspace,
  onboarding: OnboardingWorkspace,
  xero: XeroReconciliationWorkspace,
  migration: MigrationWorkspace,
  audit: AuditTrailWorkspace,
  administration: AdministrationWorkspace,
};
let callbackCompletion: ReturnType<typeof completeEntraCallback> | undefined;

function getConfiguredBrowserAuth(): SupabaseClient | undefined {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string | undefined;
  const anonymousKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;
  if (!supabaseUrl || !anonymousKey) return undefined;
  configuredBrowserAuth ??= createBrowserAuth({
    supabaseUrl,
    supabaseAnonymousKey: anonymousKey,
    redirectUrl: `${window.location.origin}/auth/callback`,
  });
  return configuredBrowserAuth;
}

export function App() {
  const [authenticationStatus, setAuthenticationStatus] = useState<
    'checking' | 'signed-in' | 'signed-out' | 'error'
  >('signed-out');
  const [accessToken, setAccessToken] = useState<string>();
  const [accountEmail, setAccountEmail] = useState<string>();
  const [accessRevision, setAccessRevision] = useState(0);
  const [crmAccess, setCrmAccess] = useState<{
    client: CrmApiClient;
    revision: number;
    status: 'active' | 'pending' | 'error';
    canSearchQuotes?: boolean;
    canWriteRecords?: boolean;
    canManage?: boolean;
    canReadAudit?: boolean;
    profitabilityAvailable?: boolean;
    documentsAvailable?: boolean;
    accountRoleLabels?: readonly string[];
  }>();
  // Cache downloaded factory code only. Each current token still gets its own client.
  const [apiCode, setApiCode] = useState<{ factory?: CrmApiFactory; failed?: boolean }>({});
  const [directoryRevision, setDirectoryRevision] = useState(0);
  const [opportunityRevision, setOpportunityRevision] = useState(0);
  const [publicOnboardingToken] = useState(() =>
    publicOnboardingTokenFromHash(window.location.hash),
  );
  const [creditRoute] = useState(() => window.location.hash.split('?')[0] === '#credit-form');
  const [creditToken] = useState(() => {
    const value = new URLSearchParams(window.location.hash.split('?')[1] ?? '').get('token') ?? '';
    return value.length <= 256 ? value : '';
  });
  const [creation, setCreation] = useState<{
    kind: ContextCreateKind;
    client: CrmApiClient;
    trigger: HTMLElement;
  }>();
  const [routeHash, setRouteHash] = useState(() => window.location.hash);
  const [navigationRevision, setNavigationRevision] = useState(0);
  const activeRoute = routeFromHash(routeHash);
  const authClient = creditRoute ? undefined : getConfiguredBrowserAuth();
  const apiUrl = import.meta.env.VITE_API_URL as string | undefined;
  const apiFactory = apiCode.factory;
  const crmClient = useMemo(
    () => (apiUrl && accessToken && apiFactory ? apiFactory(apiUrl, () => accessToken) : undefined),
    [accessToken, apiUrl, apiFactory],
  );
  // Access belongs to this exact session client and explicit retry. A repeated auth
  // event must not strand the UI in checking; a new token must never reuse old access.
  const crmAccessStatus =
    crmAccess?.client === crmClient && crmAccess?.revision === accessRevision
      ? crmAccess.status
      : 'checking';
  useEffect(() => {
    if (
      creation &&
      (creation.client !== crmClient || crmAccessStatus !== 'active' || !crmAccess?.canWriteRecords)
    ) {
      // Rendering already hides stale context immediately. Retire that exact
      // selection asynchronously so a later access retry cannot resurrect it,
      // without clearing a newly opened launcher from another session.
      queueMicrotask(() => setCreation((current) => (current === creation ? undefined : current)));
    }
  }, [creation, crmClient, crmAccessStatus, crmAccess?.canWriteRecords]);
  const closeCreation = () => {
    const trigger = creation?.trigger;
    setCreation(undefined);
    if (trigger?.isConnected) trigger.focus();
  };
  const clearCreditToken = useCallback(() => {
    window.history.replaceState({}, document.title, `${window.location.origin}/#credit-form`);
  }, []);
  const clearPublicTokenFromAddress = useCallback(() => {
    window.history.replaceState({}, document.title, `${window.location.origin}/#onboarding-form`);
  }, []);

  useEffect(() => {
    let acceptedHash = window.location.hash;
    const handleRouteChange = (event: HashChangeEvent) => {
      // A later route may already be queued; never restore a previously rejected URL.
      if (event.newURL && new URL(event.newURL).hash !== window.location.hash) return;
      if (!canNavigate()) {
        const previousHash = rejectedNavigationHash() ?? acceptedHash;
        window.history.replaceState(null, '', previousHash || '#overview');
        return;
      }
      acceptedHash = window.location.hash;
      setCreation(undefined);
      setRouteHash(window.location.hash);
      setNavigationRevision((revision) => revision + 1);
    };
    window.addEventListener('hashchange', handleRouteChange);
    return () => window.removeEventListener('hashchange', handleRouteChange);
  }, []);

  useEffect(() => {
    if (!authClient || publicOnboardingToken) return;
    let active = true;
    let authEventVersion = 0;

    async function initializeAuthentication() {
      if (window.location.pathname === '/auth/callback') {
        if (active) setAuthenticationStatus('checking');
        callbackCompletion ??= completeEntraCallback(
          authClient as SupabaseClient,
          window.location.href,
        );
        const callback = await callbackCompletion;
        if (callback.cleanUrl) window.history.replaceState({}, document.title, callback.cleanUrl);
      }

      const initialVersion = authEventVersion;
      const { data, error } = await (authClient as SupabaseClient).auth
        .getSession()
        .catch((error: unknown) => {
          // A stale failed read must not erase a newer auth event either.
          if (active && initialVersion === authEventVersion) throw error;
          return { data: { session: null }, error: null };
        });
      if (!active || initialVersion !== authEventVersion) return;
      if (error) throw error;
      if (active) {
        setAccessToken(data.session?.access_token);
        setAccountEmail(data.session?.user?.email);
        setAuthenticationStatus(data.session ? 'signed-in' : 'signed-out');
      }
    }

    void initializeAuthentication().catch(() => {
      window.history.replaceState({}, document.title, `${window.location.origin}/`);
      if (active) {
        setAccessToken(undefined);
        setAccountEmail(undefined);
        setAuthenticationStatus('error');
      }
    });

    const { data } = authClient.auth.onAuthStateChange((_event, session) => {
      if (active) {
        authEventVersion += 1;
        setAccessToken(session?.access_token);
        setAccountEmail(session?.user?.email);
        setAuthenticationStatus(session ? 'signed-in' : 'signed-out');
      }
    });

    return () => {
      active = false;
      data.subscription.unsubscribe();
    };
  }, [authClient, publicOnboardingToken]);

  useEffect(() => {
    if (
      authenticationStatus !== 'signed-in' ||
      !apiUrl ||
      !accessToken ||
      apiCode.factory ||
      publicOnboardingToken ||
      creditRoute
    )
      return;
    let active = true;
    void loadCrmApiFactory().then(
      (factory) => {
        if (active) setApiCode({ factory });
      },
      () => {
        if (active) setApiCode({ failed: true });
      },
    );
    return () => {
      active = false;
    };
  }, [
    authenticationStatus,
    apiUrl,
    accessToken,
    apiCode.factory,
    accessRevision,
    publicOnboardingToken,
    creditRoute,
  ]);

  useEffect(() => {
    if (authenticationStatus !== 'signed-in' || !crmClient) {
      return;
    }
    let active = true;
    void crmClient
      .getSession()
      .then((session) => {
        if (active)
          setCrmAccess({
            client: crmClient,
            revision: accessRevision,
            status: session.roles.length ? 'active' : 'pending',
            canSearchQuotes: hasCapability(session.roles, 'integration.xero.sync'),
            canWriteRecords: hasCapability(session.roles, 'records.write'),
            canManage: hasCapability(session.roles, 'configuration.manage'),
            canReadAudit: hasCapability(session.roles, 'audit.read'),
            accountRoleLabels: session.roles.map(
              (role) =>
                ({
                  admin: 'Administrator',
                  executive: 'Executive / Director',
                  finance_credit_approver: 'Finance / Credit Approver',
                  sales_account_manager: 'Sales / Account Manager',
                  technical_delivery: 'Technical / Delivery',
                  read_only_auditor: 'Read-only / Auditor',
                })[role] ?? readableCode(role),
            ),
            documentsAvailable:
              session.opportunityDocumentsEnabled === true &&
              hasCapability(session.roles, 'records.read'),
            profitabilityAvailable:
              session.profitabilityEnabled === true &&
              hasCapability(session.roles, 'profitability.read'),
          });
      })
      .catch(() => {
        if (active) setCrmAccess({ client: crmClient, revision: accessRevision, status: 'error' });
      });
    return () => {
      active = false;
    };
  }, [authenticationStatus, crmClient, accessRevision]);

  useEffect(() => {
    if (
      authenticationStatus === 'signed-in' &&
      crmClient &&
      crmAccessStatus === 'checking' &&
      !creditRoute &&
      !publicOnboardingToken
    ) {
      // Download code while access is checked; mounting and all workspace reads
      // still wait for the exact client's successful CRM access result.
      void routeWorkspaces[activeRoute]?.preload();
    }
  }, [
    authenticationStatus,
    crmClient,
    crmAccessStatus,
    activeRoute,
    creditRoute,
    publicOnboardingToken,
  ]);

  async function handleSignIn(): Promise<void> {
    if (!authClient) throw new Error('Authentication is not configured');
    await signInWithEntra(authClient, `${window.location.origin}/auth/callback`);
  }

  async function handleSignOut(): Promise<void> {
    if (!authClient) return;
    await authClient.auth.signOut({ scope: 'local' });
    setAccessToken(undefined);
    setAccountEmail(undefined);
    setCrmAccess(undefined);
    setAuthenticationStatus('signed-out');
  }

  if (creditRoute)
    return (
      <div className="public-onboarding-shell">
        <header className="public-onboarding-header">
          <a className="brand" href="#credit-form" aria-label="Resilience credit application home">
            <span className="brand-mark" aria-hidden="true" />
            <span>
              <strong>Resilience</strong>
              <small>Customer credit application</small>
            </span>
          </a>
          <span className="phase-badge">Synthetic preview</span>
        </header>
        <main id="main-content" tabIndex={-1}>
          {apiUrl ? (
            <WorkspaceErrorBoundary>
              <PublicIntakeEntry
                kind="credit"
                apiUrl={apiUrl}
                Form={PublicCreditForm}
                token={creditToken}
                onTokenResolved={clearCreditToken}
              />
            </WorkspaceErrorBoundary>
          ) : (
            <AsyncState state="error" message="Credit intake is unavailable." />
          )}
        </main>
        <footer>Resilience · Independent credit review · No automatic approval</footer>
      </div>
    );

  if (publicOnboardingToken) {
    return (
      <div className="public-onboarding-shell">
        <header className="public-onboarding-header">
          <a className="brand" href="#onboarding-form" aria-label="Resilience onboarding home">
            <span className="brand-mark" aria-hidden="true" />
            <span>
              <strong>Resilience</strong>
              <small>Customer onboarding</small>
            </span>
          </a>
          <span className="phase-badge">Synthetic preview</span>
        </header>
        <main id="main-content" tabIndex={-1}>
          {apiUrl ? (
            <WorkspaceErrorBoundary>
              <PublicIntakeEntry
                kind="onboarding"
                apiUrl={apiUrl}
                Form={PublicOnboardingForm}
                token={publicOnboardingToken}
                onTokenResolved={clearPublicTokenFromAddress}
              />
            </WorkspaceErrorBoundary>
          ) : (
            <AsyncState state="error" message="This onboarding preview is not configured." />
          )}
        </main>
        <footer>
          <span>Resilience onboarding</span>
          <span>No files, credit decisions, or automatic CRM records</span>
        </footer>
      </div>
    );
  }

  return (
    <AppShell
      documentsAvailable={crmAccessStatus === 'active' && crmAccess?.documentsAvailable === true}
      profitabilityAvailable={
        crmAccessStatus === 'active' && crmAccess?.profitabilityAvailable === true
      }
      accountEmail={authenticationStatus === 'signed-in' ? accountEmail : undefined}
      accountRoleLabels={crmAccessStatus === 'active' ? crmAccess?.accountRoleLabels : undefined}
      search={
        authenticationStatus === 'signed-in' && crmClient && crmAccessStatus === 'active' ? (
          <GlobalSearch
            key={accessToken}
            client={crmClient}
            canSearchQuotes={crmAccess?.canSearchQuotes ?? false}
          />
        ) : undefined
      }
      onSignIn={handleSignIn}
      onSignOut={handleSignOut}
      authenticationStatus={authenticationStatus}
      activeRoute={activeRoute}
      onRouteIntent={
        crmAccessStatus === 'active' ? (route) => void routeWorkspaces[route]?.preload() : undefined
      }
      visibleRoutes={
        crmAccessStatus === 'active'
          ? [
              'overview',
              'organisations',
              'pipeline',
              'opportunities',
              'tasks',
              'reports',
              'catalogue',
              'team',
              'onboarding',
              ...(crmAccess?.canSearchQuotes ? ['xero' as const] : []),
              ...(crmAccess?.canReadAudit ? ['audit' as const] : []),
              ...(crmAccess?.canManage ? ['migration' as const, 'administration' as const] : []),
            ]
          : []
      }
      onCreateChild={
        crmAccessStatus === 'active' && crmAccess?.canWriteRecords && crmClient
          ? (kind, trigger) => setCreation({ kind, client: crmClient, trigger })
          : undefined
      }
      createRoutes={
        crmAccessStatus === 'active' && crmAccess?.canWriteRecords
          ? ['organisations', 'opportunities']
          : []
      }
    >
      {creation &&
      creation.client === crmClient &&
      crmAccessStatus === 'active' &&
      crmAccess?.canWriteRecords ? (
        <CreationLauncher
          key={accessToken + creation.kind}
          kind={creation.kind}
          client={creation.client}
          onClose={closeCreation}
          onContinue={(hash) => {
            if (window.location.hash === hash)
              window.dispatchEvent(new HashChangeEvent('hashchange'));
            else window.location.hash = hash;
          }}
        />
      ) : null}
      <WorkspaceErrorBoundary
        key={`${authenticationStatus}:${activeRoute === 'reports' ? '#reports' : `${routeHash}:${navigationRevision}`}`}
      >
        {authenticationStatus === 'signed-in' ? (
          crmClient ? (
            crmAccessStatus === 'checking' ? (
              <section className="content-section">
                <AsyncState state="loading" message="Checking CRM access…" />
              </section>
            ) : crmAccessStatus === 'pending' ? (
              <section className="content-section" aria-labelledby="access-pending-heading">
                <span className="eyebrow">Controlled workforce activation</span>
                <h1 id="access-pending-heading">Your CRM access is awaiting approval.</h1>
                <p>
                  Microsoft sign-in succeeded. A CRM administrator must now approve your role before
                  records become available. You can safely close this page and return after the
                  administrator confirms activation.
                </p>
              </section>
            ) : crmAccessStatus === 'error' ? (
              <section className="content-section">
                <AsyncState
                  state="error"
                  message="CRM access could not be checked. Try again, or sign out if your session has expired."
                  onRetry={() => setAccessRevision((current) => current + 1)}
                />
              </section>
            ) : activeRoute === 'overview' ? (
              <OverviewDashboard
                readOnly={!crmAccess?.canWriteRecords}
                client={crmClient}
                directoryRevision={directoryRevision}
                opportunityRevision={opportunityRevision}
              />
            ) : activeRoute === 'reports' ? (
              <ReportsWorkspace
                client={crmClient}
                navigationRevision={navigationRevision}
                templateTarget={
                  new URLSearchParams(routeHash.split('?')[1]).get('templateId') ?? ''
                }
                directoryRevision={directoryRevision}
                opportunityRevision={opportunityRevision}
              />
            ) : activeRoute === 'opportunities' ||
              ((activeRoute === 'pipeline' || activeRoute === 'tasks') &&
                /^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i.test(
                  new URLSearchParams(routeHash.split('?')[1]).get('opportunityId') ?? '',
                ) &&
                (activeRoute !== 'tasks' ||
                  !new URLSearchParams(routeHash.split('?')[1]).has('taskId') ||
                  /^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i.test(
                    new URLSearchParams(routeHash.split('?')[1]).get('taskId') ?? '',
                  ))) ? (
              <OpportunityWorkspace
                client={crmClient}
                directoryRevision={directoryRevision}
                onOpportunityChanged={() => setOpportunityRevision((current) => current + 1)}
              />
            ) : activeRoute === 'pipeline' ? (
              <PipelineReviewWorkspace
                client={crmClient}
                directoryRevision={directoryRevision}
                opportunityRevision={opportunityRevision}
              />
            ) : activeRoute === 'tasks' ? (
              <TaskWorkspace
                client={crmClient}
                opportunityRevision={opportunityRevision}
                onTaskChanged={() => setOpportunityRevision((current) => current + 1)}
              />
            ) : activeRoute === 'catalogue' ? (
              <CatalogWorkspace client={crmClient} />
            ) : activeRoute === 'team' ? (
              <CapabilityWorkspace client={crmClient} />
            ) : activeRoute === 'organisations' ? (
              <DirectoryWorkspace
                client={crmClient}
                onDirectoryChanged={() => setDirectoryRevision((current) => current + 1)}
              />
            ) : activeRoute === 'onboarding' ? (
              <OnboardingWorkspace client={crmClient} />
            ) : activeRoute === 'xero' ? (
              <XeroReconciliationWorkspace client={crmClient} />
            ) : activeRoute === 'administration' ? (
              <AdministrationWorkspace client={crmClient} />
            ) : activeRoute === 'audit' ? (
              <AuditTrailWorkspace client={crmClient} />
            ) : (
              <MigrationWorkspace client={crmClient} />
            )
          ) : (
            <section className="content-section">
              {!apiUrl ? (
                <AsyncState
                  state="error"
                  message="The CRM API is not configured for this environment. No data was requested."
                />
              ) : apiCode.failed ? (
                <AsyncState
                  state="error"
                  message="The CRM connection could not be loaded. Check your connection and try again."
                  onRetry={() => {
                    setApiCode({});
                    setAccessRevision((current) => current + 1);
                  }}
                />
              ) : (
                <AsyncState state="loading" message="Preparing CRM connection…" />
              )}
            </section>
          )
        ) : (
          <>
            <section className="hero" id="overview" aria-labelledby="overview-heading">
              <div>
                <span className="eyebrow">Welcome to Resilience CRM</span>
                <h1 id="overview-heading">Your customers, pipeline and team in one place.</h1>
                <p>Sign in with your Resilience Microsoft account to continue.</p>
              </div>
            </section>
          </>
        )}
      </WorkspaceErrorBoundary>
    </AppShell>
  );
}
